import "dayjs/locale/tr";

import { ConfigProvider } from "antd";
import Router from "./router/Router";
import type { ThemeConfig } from "antd";

import trTR from "antd/locale/tr_TR";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import AuthProvider from "./providers/AuthProvider";
import { FeatureFlagProvider } from "./providers/FeatureFlagProvider";
import { RefreshModalProvider } from "./providers/RefreshModalProvider";
import { globalErrorHandler } from "./utils/errorHandler";
import * as Sentry from "@sentry/react";

dayjs.extend(localeData);
dayjs.extend(weekday);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(advancedFormat); // "wo"/"gggg" (hafta) format tokenları için
dayjs.locale("tr");

const theme: ThemeConfig = {
  token: {
    controlHeight: 38,
  },
  components: {
    Layout: {
      bodyBg: "white",
    },
    Segmented: {
      itemSelectedColor: "#1677ff",
    },
  },
};

function App() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        retry: (failureCount, error) => {
          // Don't retry on 401 errors — http.ts already
          // attempted a token refresh and retry internally
          if (globalErrorHandler.is401Error(error)) {
            return false;
          }
          return failureCount < 1;
        },
        retryDelay: 2000,
        refetchIntervalInBackground: true,
      },
      mutations: {
        retry: (failureCount, error) => {
          if (globalErrorHandler.is401Error(error)) {
            return false;
          }
          return failureCount < 1;
        },
      },
    },
  });

  return (
    <ConfigProvider locale={trTR} theme={theme}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <FeatureFlagProvider>
            <RefreshModalProvider>
              <Router />
            </RefreshModalProvider>
          </FeatureFlagProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ConfigProvider>
  );
}

const AppWithProfiler = Sentry.withProfiler(App);
AppWithProfiler.displayName = "AppWithProfiler";

export default AppWithProfiler;
